import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  Button,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Pagination
} from 'react-bootstrap';
import { fetchExportLogs } from 'api/endpoints/Export';
import DownloadModal from './DownloadModal';

const POLLING_INTERVAL = 5000; // 5 seconds

const ExportHistoryTable = ({ refreshTrigger, setRefreshTrigger }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalShow, setModalShow] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchHistory = useCallback(
    async (isPolling = false) => {
      if (!isPolling) setLoading(true);
      try {
        const response = await fetchExportLogs(
          (page - 1) * 10,
          10,
          'farmland_report',
          {}
        );

        if (response?.data?.results?.items) {
          setData(response.data.results.items);
          setTotalPages(
            Math.ceil((response.data.results.totalCount || 0) / 10)
          );
        } else if (response?.data?.results?.data) {
          setData(response.data.results.data);
          setTotalPages(
            Math.ceil((response.data.results.totalCount || 0) / 10)
          );
        } else if (response?.data?.items) {
          setData(response.data.items);
          setTotalPages(Math.ceil((response.data.totalCount || 0) / 10));
        } else if (response?.data?.data) {
          setData(response.data.data);
          if (response.data.totalCount) {
            setTotalPages(Math.ceil(response.data.totalCount / 10));
          }
        } else if (Array.isArray(response?.data)) {
          setData(response.data);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('Failed to fetch export history:', error);
      } finally {
        if (!isPolling) setLoading(false);
      }
    },
    [page]
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchHistory(true);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchHistory]);

  const handleDownloadClick = record => {
    setSelectedRecord(record);
    setModalShow(true);
  };

  const renderStatus = record => {
    const status = (record.status || '').toLowerCase();

    if (
      status === 'completed' ||
      status === 'ready' ||
      status === 'ready to download'
    ) {
      return (
        <Button
          variant="link"
          className="p-0 text-success fw-bold text-decoration-none"
          onClick={() => handleDownloadClick(record)}
        >
          Ready to Download
        </Button>
      );
    }

    if (status === 'failed' || status === 'error') {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {record.error_message ||
                'Export failed due to server error or timeout'}
            </Tooltip>
          }
        >
          <span className="text-danger fw-bold" style={{ cursor: 'pointer' }}>
            Failed
          </span>
        </OverlayTrigger>
      );
    }

    // Default to Downloading if pending/processing
    let progressStr = '';
    const processed = record.processed_count || 0;
    const total = record.total_count || 0;

    if (total > 0) {
      // Format in batches of 50 for UI readability as requested
      const batchCount = Math.min(Math.ceil(processed / 50) * 50, total);
      const displayCount = total < 50 ? processed : batchCount;
      progressStr = ` (${displayCount}/${total})`;
    }

    return (
      <span className="text-warning fw-bold">
        <Spinner animation="border" size="sm" className="me-2" />
        Downloading{progressStr}
      </span>
    );
  };

  const formatDateTime = dateStr => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const day = String(date.getDate()).padStart(2, '0');
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => fetchHistory()}
        >
          <i className="fas fa-sync-alt me-1"></i> Refresh
        </Button>
      </div>

      <div
        className="table-responsive"
        style={{
          maxHeight: '600px',
          overflowY: 'auto',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}
      >
        <Table
          hover
          className="align-middle mb-0"
          style={{ minWidth: '1200px' }}
        >
          <thead
            className="table-light sticky-top"
            style={{ zIndex: 1, backgroundColor: '#f8f9fa' }}
          >
            <tr>
              <th style={{ width: '250px' }}>File Name</th>
              <th style={{ width: '180px' }}>Date & Time</th>
              <th>Farmer Name</th>
              <th>Farm Name</th>
              <th>Subcounty</th>
              <th>Created Date</th>
              <th>Modified Date</th>
              <th style={{ width: '180px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center py-5">
                  <Spinner animation="border" variant="success" />
                  <div className="mt-2 text-muted small">
                    Loading history...
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-5 text-muted">
                  <i className="fas fa-history fa-2x mb-2 d-block opacity-25"></i>
                  No export history found.
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={row.id || idx}>
                  <td className="text-break small font-monospace">
                    {row.file_name || `Farm_Report_${idx}.csv`}
                  </td>
                  <td className="small">
                    {formatDateTime(
                      row.created_at ||
                        row.updated_at ||
                        row.saved_on ||
                        row.timestamp ||
                        row.created_on
                    )}
                  </td>
                  <td className="small">
                    {row.filters?.farmer_name_display ||
                      row.filters?.farmer_name ||
                      'ALL'}
                  </td>
                  <td className="small">
                    {row.filters?.farm_name_display ||
                      row.filters?.farm_name ||
                      'ALL'}
                  </td>
                  <td className="small">
                    {row.filters?.sub_county_display ||
                      row.filters?.sub_county ||
                      'ALL'}
                  </td>
                  <td className="small">
                    {row.filters?.created_date_display ||
                      (row.filters?.created_start_date &&
                      row.filters?.created_end_date
                        ? `${row.filters.created_start_date} to ${row.filters.created_end_date}`
                        : 'ALL')}
                  </td>
                  <td className="small">
                    {row.filters?.modified_date_display ||
                      (row.filters?.modified_start_date &&
                      row.filters?.modified_end_date
                        ? `${row.filters.modified_start_date} to ${row.filters.modified_end_date}`
                        : 'ALL')}
                  </td>
                  <td className="small">{renderStatus(row)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination className="justify-content-center mt-3 custom-pagination">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={i + 1 === page}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          />
        </Pagination>
      )}

      {modalShow && selectedRecord && (
        <DownloadModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          record={selectedRecord}
        />
      )}

      <style>{`
        .sticky-top {
          top: 0;
          box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
        }
        .table-responsive::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .table-responsive::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 4px;
        }
        .table-responsive::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
      `}</style>
    </div>
  );
};

ExportHistoryTable.propTypes = {
  refreshTrigger: PropTypes.number.isRequired,
  setRefreshTrigger: PropTypes.func.isRequired
};

export default ExportHistoryTable;
